import { BeforePlugin, EnrichmentPlugin, Event } from '@amplitude/analytics-types'
import debounce from 'lodash/debounce'
import snakeCase from 'lodash/snakeCase'
import trim from 'lodash/trim'

import { ampli, AmplitudePageViewed, GalleryInteracted, NavLinkClick, PageExited } from '@Analytics/ampli'
import { Config } from '@Analytics/config'
import consent from '@Analytics/consent'
import { getAttributionUserAttributes, getReferrerAttributes, getSplitTests, getURLAttributes, isLandingPage } from '@Analytics/helpers'
import offline from '@Analytics/offline'
import { getDeviceType } from '@Globals/Device'

export function pauseEventStreamPlugin(): BeforePlugin {
  let initialized = false
  let setPaused: (isPaused: boolean) => void

  function updatePausedStatus() {
    if (!setPaused) {
      return
    }

    setPaused(consent.hasConsent === false || offline.isOffline === true)
  }

  return {
    name: 'pause-event-stream',
    type: 'before',

    async setup(config, client) {
      setPaused = (status) => {
        // eslint-disable-next-line no-param-reassign
        config.offline = status

        if (status === false) {
          client.flush()
        }
      }

      initialized = false
      consent.addEventListener(updatePausedStatus)
      offline.addEventListener(updatePausedStatus)
    },

    async teardown() {
      initialized = false
      consent.removeEventListener(updatePausedStatus)
      offline.removeEventListener(updatePausedStatus)
    },

    async execute(event: Event) {
      // to ensure the default offline plugin is not enabled, we must set it to OfflineDisabled
      // to avoid sending any events before we have consent, we must set offline status before the initial event
      if (!initialized) {
        initialized = true
        consent.check()
        offline.check()
        updatePausedStatus()
      }

      return event
    },
  }
}

let identifyPromise: Promise<void> | undefined

export function waitForIdentifyEnrichment(): BeforePlugin {
  return {
    name: 'wait-for-identify-enrichment',
    type: 'before',

    async execute(event: Event) {
      // don't block identify events
      if (event.event_type === '$identify') {
        return event
      }

      if (!identifyPromise) {
        identifyPromise = (async () => {
          const splitTests = getSplitTests()
          const userAttributes = {
            ...getAttributionUserAttributes(),
            ...getReferrerAttributes(),
            ...getURLAttributes(),
            trendhim_ecom_split_tests: splitTests !== '' ? splitTests.split(',') : [],
          }

          const searchParams = new URLSearchParams(window.location.search)
          const customerID = searchParams.get('thcid') || undefined

          // only identify when either user attributes or customer id is available
          if (Object.keys(userAttributes).length === 0 && !customerID) {
            return
          }

          // don't wait for the identify call to finish using promises, as it blocks the whole amplitude timeline queue
          // instead we flush the current event, to ensure it goes to the top of the queue
          ampli.identify(customerID, userAttributes)
          ampli.flush()
        })()
      }

      await identifyPromise

      return event
    },
  }
}

export function baseAttributeEnrichment(config: Config): EnrichmentPlugin {
  return {
    name: 'base-attribute-enrichment',
    type: 'enrichment',

    async execute(event: Event) {
      // TODO: we need these for all custom events, but not internal amplitude events. Look into better matching options
      if (event.event_type.startsWith('$')) {
        return event
      }

      const pageID = config.page.id || ''

      // eslint-disable-next-line no-param-reassign
      event.event_properties = {
        ...event.event_properties,
        country: config.store?.config?.countryCode || 'unknown',
        device_browser_width: window.innerWidth,
        device_type: getDeviceType(),
        query_parameters: window.location.search.replace('?', ''),
        split_tests: getSplitTests(),
        src_page_type: snakeCase(config.page.type || 'other'),
        src_page_type_id: config.page.id || '',
        src_page_type_id_unix: `${Math.floor(Date.now() / 1000)}${pageID ? `-${pageID}` : ''}`,
        store_id: config.store?.id || 'unknown',
      }

      return event
    },
  }
}

export function pageViewTrackingEnrichment(config: Config): EnrichmentPlugin {
  return {
    name: 'page-view-tracking-enrichment',
    type: 'enrichment',

    async execute(event: AmplitudePageViewed) {
      if (event.event_type !== '[Amplitude] Page Viewed') {
        return event
      }

      // eslint-disable-next-line no-param-reassign
      event.event_properties = {
        ...getReferrerAttributes(),
        ...getURLAttributes(),
        is_landing_page: isLandingPage,
        ...config.page.attributes,
        ...event.event_properties,
      }

      return event
    },
  }
}

export function normalizationAttributeEnrichment(): EnrichmentPlugin {
  const attributes = [
    'delivery_option_key',
    'filter_code',
    'filter_option_id',
    'guide_info_link_type',
    'payment_option_sub_id',
    'payment_provider',
    'selected_country',
    'source_id',
  ]

  return {
    name: 'normalization-attribute-enrichment',
    type: 'enrichment',

    async execute(event: Event) {
      if (!event.event_properties) {
        return event
      }

      attributes.forEach((attribute) => {
        const value = event.event_properties![attribute]
        if (!value || typeof value !== 'string') {
          return
        }

        // eslint-disable-next-line no-param-reassign
        event.event_properties![attribute] = snakeCase(trim(value))
      })

      return event
    },
  }
}

export function inViewEnrichment(): EnrichmentPlugin {
  if (typeof IntersectionObserver === 'undefined' || typeof MutationObserver === 'undefined') {
    return {}
  }

  let cachedScrollMetrics: ReturnType<typeof calculateScrollMetrics> = false

  function calculateScrollMetrics() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const totalHeight = document.documentElement.scrollHeight

    const scrollProgress = (scrollTop / (totalHeight - viewportHeight)) * 100
    const coverageProgress = ((scrollTop + viewportHeight) / totalHeight) * 100

    const metrics = {
      scroll: Math.min(100, Math.max(0, Math.round(scrollProgress))),
      content: Math.min(100, Math.max(0, Math.round(coverageProgress))),
    }

    if (Number.isNaN(metrics.scroll) || Number.isNaN(metrics.content)) {
      return false
    }

    cachedScrollMetrics = metrics

    return metrics
  }

  setTimeout(calculateScrollMetrics, 1)
  window.addEventListener('scroll', debounce(calculateScrollMetrics, 100), { passive: true })

  function getScrollMetrics() {
    const metrics = calculateScrollMetrics()
    if (!metrics) {
      // calculating metrics fails when <dialog>'s are open - instead of spending too much time on it, we simply use the cache
      return cachedScrollMetrics
    }

    return metrics
  }

  const activeIntersections = new Set<string>()
  const intersectionHistory = new Set<string>()

  const intersectionObserver = new IntersectionObserver((i) => {
    i.forEach((intersection) => {
      const target = intersection.target as HTMLElement
      if (!target.dataset.inView) {
        return
      }

      calculateScrollMetrics()

      if (!intersection.isIntersecting) {
        activeIntersections.delete(target.dataset.inView)
        return
      }

      activeIntersections.add(target.dataset.inView)
      intersectionHistory.add(target.dataset.inView)
    })
  }, {
    threshold: 0.1,
  })

  function trackElement(target: HTMLElement) { intersectionObserver.observe(target) }

  // track all elements that are added dynamically
  const mutationObserver = new MutationObserver((m) => {
    m.forEach((mutation) => {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'data-in-view') {
        return
      }

      trackElement(mutation.target as HTMLElement)
    })
  })
  mutationObserver.observe(document.body, {
    attributeFilter: ['data-in-view'],
    subtree: true,
  })

  // track all existing elements
  document.querySelectorAll('[data-in-view]').forEach(trackElement)

  return {
    name: 'in-view-enrichment',
    type: 'enrichment',

    async execute(event: NavLinkClick) {
      if (event.event_type !== 'cart_add_click'
        && event.event_type !== 'eCommerce - AddToCart - Item'
        && event.event_type !== 'nav_link_click'
        && event.event_type !== 'page_exited'
        && event.event_type !== 'prompt_open'
      ) {
        return event
      }

      const metrics = getScrollMetrics()
      if (!metrics) {
        return event
      }

      /* eslint-disable no-param-reassign */
      event.event_properties!.in_view = Array.from(activeIntersections)
      event.event_properties!.in_view_history = Array.from(intersectionHistory)
      event.event_properties!.content_percentage = metrics.content
      event.event_properties!.scroll_percentage = metrics.scroll
      /* eslint-enable no-param-reassign */

      return event
    },
  }
}

export function galleryInteractionsEnrichment(config: Config): EnrichmentPlugin {
  let count = 0
  const assetTypes = new Set<string>()

  function isGalleryInteracted(event: Event): event is GalleryInteracted {
    return event.event_type === 'gallery_interacted'
  }

  function isPageExited(event: Event): event is PageExited {
    return event.event_type === 'page_exited'
  }

  return {
    name: 'page-view-tracking-enrichment',
    type: 'enrichment',

    async execute(event: GalleryInteracted | PageExited) {
      if (isGalleryInteracted(event)) {
        count += 1
        if (event.event_properties?.asset_type) {
          assetTypes.add(event.event_properties.asset_type)
        }

        if (count > 1) {
          return null
        }

        /* eslint-disable no-param-reassign */
        event.event_properties!.product_type = config.page.attributes?.product_type
        event.event_properties!.attribute_type = config.page.attributes?.attribute_type
        event.event_properties!.attribute_sub_type = config.page.attributes?.attribute_sub_type
        /* eslint-enable no-param-reassign */

        return event
      }

      if (isPageExited(event)) {
        /* eslint-disable no-param-reassign */
        event.event_properties!.gallery_interaction_asset_types = Array.from(assetTypes)
        event.event_properties!.gallery_interactions = count
        /* eslint-enable no-param-reassign */
        return event
      }

      return event
    },
  }
}
